// El catálogo de la tienda conectada, en las primeras piezas
// (ADR-2026-10-02-onboarding-v2-piezas-con-cumbre-y-tienda).
//
// Al conectar Tiendanube, el import (`traerCatalogoAlConectar`) manda los productos al
// backend y vuelve enseguida: los productos van apareciendo en Atlas a medida que la cola los
// crea, y el backend no expone cuántos van. Acá se cruza lo que HAY en la tienda (la lista de
// Tiendanube) con lo que YA LLEGÓ a Atlas (`productos.tn_product_id`), para mostrar los que se
// pueden elegir y cuántos faltan.

import { supabase } from '../supabase';
import type { TnProduct } from '../tiendaNube';

/** Un producto de la tienda. `varianteId` existe sólo cuando ya llegó a Atlas: recién ahí se puede generar con él. */
export interface ProductoDeLaTienda {
  /** Única en la lista: el id de Tiendanube, o las publicaciones de Mercado Libre (`ml:…`). */
  clave: string;
  nombre: string;
  imagen: string;
  varianteId: string | null;
}

export interface CatalogoDeLaTienda {
  /** Los que llegaron, en el orden de la tienda. */
  llegados: ProductoDeLaTienda[];
  total: number;
  faltan: number;
}

const sinTildes = (t: string) => t.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

/**
 * Los productos cuyo nombre tiene TODAS las palabras buscadas, sin importar mayúsculas ni
 * tildes ("vestido rojo" encuentra "Vestido Rojo Lino"). Sin texto, todos.
 */
export function filtrarPorNombre<T extends { nombre: string }>(productos: readonly T[], texto: string): T[] {
  const palabras = sinTildes(texto).split(/\s+/).filter(Boolean);
  if (!palabras.length) return [...productos];
  return productos.filter((p) => {
    const nombre = sinTildes(p.nombre);
    return palabras.every((w) => nombre.includes(w));
  });
}

/** Si no llega nada nuevo en este tiempo, se deja de prometer que están llegando. */
// Con cientos de productos, el import manda tandas de 25 que hacen cola con el análisis de cada
// producto (misma cola): entre una tanda y la siguiente pueden pasar minutos sin novedades.
export const MS_SIN_NOVEDAD = 5 * 60_000;

/** Cuánto se sigue mirando como mucho: lo que llegue después aparece en Mi marca. */
export const MS_MAXIMO_MIRANDO = 30 * 60_000;

/** Cada cuánto se vuelve a mirar: seguido al principio, más espaciado después y cuando se demora. */
export function pausaDelSondeo(msDesdeQueAbrio: number, carga: EstadoDeLaCarga): number {
  if (carga === 'demorado') return 15_000;
  return msDesdeQueAbrio < 2 * 60_000 ? 3_000 : 6_000;
}

/**
 * Lo que dice la pantalla de la carga: cuántos ya se pueden elegir y que el resto se está
 * analizando y cargando (una tienda puede tener cientos y no llegan todos juntos).
 */
export function textoDeLaCarga(p: {
  llegados: number;
  total: number;
  faltan: number;
  carga: EstadoDeLaCarga;
  tienda: string | null;
}): string {
  const deLaTienda = p.tienda ? ` de ${p.tienda}` : '';
  if (p.carga === 'completo') {
    return p.total === 1 ? `Llegó tu producto${deLaTienda}.` : `Llegaron tus ${p.total} productos${deLaTienda}.`;
  }
  if (p.llegados === 0) {
    return `Estoy analizando y cargando tus ${p.total} productos${deLaTienda}: van a ir apareciendo aquí.`;
  }
  const listos =
    p.llegados === 1
      ? `1 de tus ${p.total} productos ya está para elegir.`
      : `${p.llegados} de tus ${p.total} productos ya están para elegir.`;
  if (p.carga === 'demorado') {
    return `${listos} ${p.faltan === 1 ? 'El que falta está' : `Los otros ${p.faltan} están`} tardando más: siguen cargándose y aparecen aquí y en Mi marca cuando estén.`;
  }
  return `${listos} ${
    p.faltan === 1
      ? 'El que falta se está analizando y cargando: aparece en la lista apenas esté.'
      : `Los otros ${p.faltan} se están analizando y cargando: van apareciendo en la lista.`
  }`;
}

export type EstadoDeLaCarga = 'completo' | 'cargando' | 'demorado';

/**
 * De qué canal salen los productos de las piezas si marcó más de uno: el primero que marcó y no
 * quedó sin tienda (ADR-2026-10-19-mercadolibre-en-las-primeras-piezas). Mientras ese se revisa, se
 * espera, para no mostrar uno y saltar al otro.
 */
export function tiendaDeLasPiezas<T extends { estado: string }>(
  orden: readonly string[],
  porCanal: Partial<Record<string, T>>,
): T | { estado: 'sin-tienda' } {
  for (const canal of orden) {
    const t = porCanal[canal];
    if (t && t.estado !== 'sin-tienda') return t;
  }
  return { estado: 'sin-tienda' };
}

/** Junta la tienda con lo que ya llegó (`tn_product_id` → variante). */
export function cruzarCatalogo(tienda: readonly TnProduct[], llegados: ReadonlyMap<number, string>): CatalogoDeLaTienda {
  const lista = tienda
    .filter((p) => llegados.has(p.id))
    .map((p) => ({ clave: String(p.id), nombre: p.nombre, imagen: p.img_src || p.imgs[0] || '', varianteId: llegados.get(p.id) ?? null }));
  return { llegados: lista, total: tienda.length, faltan: tienda.length - lista.length };
}

/** Cómo va la carga: completa, llegando, o quieta hace rato (el resto, si llega, aparece en Mi marca). */
export function estadoDeLaCarga(p: { faltan: number; msSinNovedad: number }): EstadoDeLaCarga {
  if (p.faltan <= 0) return 'completo';
  return p.msSinNovedad >= MS_SIN_NOVEDAD ? 'demorado' : 'cargando';
}

const TANDA = 200;

/**
 * Qué productos de la tienda ya están en Atlas: `tn_product_id` → su primera variante activa.
 * Mismo criterio de marca que el catálogo de la app (la marca, o sin marca).
 */
export async function leerLlegados(companyId: string, brandId: string, tnIds: readonly number[]): Promise<Map<number, string>> {
  const llegados = new Map<number, string>();
  for (let i = 0; i < tnIds.length; i += TANDA) {
    const { data, error } = await supabase
      .from('variantes')
      .select('id, created_at, productos!inner(tn_product_id, brand_id)')
      .eq('id_company', companyId)
      .eq('activo', true)
      .in('productos.tn_product_id', tnIds.slice(i, i + TANDA))
      .or(`brand_id.eq.${brandId},brand_id.is.null`, { referencedTable: 'productos' })
      .order('created_at', { ascending: true });
    if (error) throw error;
    // Muchos a uno: llega como objeto, aunque los tipos generados lo den como lista.
    type Padre = { tn_product_id: number | null };
    for (const fila of (data ?? []) as unknown as Array<{ id: string; productos: Padre | Padre[] | null }>) {
      const padre = Array.isArray(fila.productos) ? fila.productos[0] : fila.productos;
      const tnId = padre?.tn_product_id;
      if (tnId != null && !llegados.has(Number(tnId))) llegados.set(Number(tnId), fila.id);
    }
  }
  return llegados;
}

/**
 * ¿Ya llegó a Atlas ALGÚN producto de la tienda para esta marca? Sin ninguno, el catálogo
 * nunca se importó (la tienda se conectó antes y por una puerta que no traía productos), y
 * el onboarding lo trae. Con alguno, no se relanza: podría haber un import todavía en cola
 * y se duplicarían productos (ADR-2026-10-02-onboarding-v2-tienda-ya-conectada-trae-el-catalogo).
 */
export async function hayProductosDeLaTienda(companyId: string, brandId: string): Promise<boolean> {
  const { count, error } = await supabase
    .from('productos')
    .select('id', { count: 'exact', head: true })
    .eq('id_company', companyId)
    .not('tn_product_id', 'is', null)
    .or(`brand_id.eq.${brandId},brand_id.is.null`);
  if (error) throw error;
  return (count ?? 0) > 0;
}

/**
 * Al entrar al paso del canal con Tiendanube: ¿ya está conectada y, si lo está, hay que traer
 * el catálogo? Se averigua TODO antes de tocar estado (review Greptile #164,
 * ADR-2026-10-07-tienda-ya-conectada-revisa-antes-de-cambiar-de-fase): si la pantalla pasa a
 * "conectado" y después pregunta por los productos, el cambio de fase reinicia el efecto, su
 * cleanup descarta la respuesta y el catálogo de una tienda conectada de antes no llegaba nunca.
 * Ante la duda (no se pudo consultar) no se trae: un import en cola duplicaría productos.
 */
export async function revisarTiendaConectada(
  p: { companyId: string | null | undefined; brandId: string | null },
  deps: {
    estado: (brandId: string | null) => Promise<{ connected: boolean; storeName?: string | null }>;
    hayProductos: (companyId: string, brandId: string) => Promise<boolean>;
  },
): Promise<{ conectada: false } | { conectada: true; tienda: string | null; traerCatalogo: boolean }> {
  const s = await deps.estado(p.brandId);
  if (!s.connected) return { conectada: false };
  const tienda = s.storeName ?? null;
  if (!p.companyId || !p.brandId) return { conectada: true, tienda, traerCatalogo: false };
  const yaHay = await deps.hayProductos(p.companyId, p.brandId).catch(() => true);
  return { conectada: true, tienda, traerCatalogo: !yaHay };
}

// ─── Modo guion (sólo DEV): una tienda de ficción que llega de a poco ─────────────

const COLORES_DEL_GUION = ['#1F3A2E', '#C8A97E', '#E4572E', '#3B5B92', '#8C6A5D', '#2F4858', '#B5838D', '#6D8A64'];

function fotoDelGuion(n: number): string {
  const color = COLORES_DEL_GUION[n % COLORES_DEL_GUION.length];
  return (
    'data:image/svg+xml;utf8,' +
    encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" width="300" height="300" viewBox="0 0 300 300"><rect width="300" height="300" fill="#F5F2EC"/><path d="M95 70h110l25 45-30 15v110H100V130l-30-15z" fill="${color}"/></svg>`,
    )
  );
}

export const TIENDA_DEL_GUION: TnProduct[] = Array.from({ length: 14 }, (_, n) => ({
  id: 900_000 + n,
  nombre: ['Camisa Oxford', 'Remera básica', 'Camisa de lino', 'Buzo frisa', 'Pantalón chino', 'Campera liviana', 'Chomba piqué'][n % 7] + (n >= 7 ? ' II' : ''),
  img_src: fotoDelGuion(n),
  imgs: [fotoDelGuion(n)],
  variantes: [],
}));

/** En el guion llegan de a uno, cada `msPorProducto`, desde que se abrió la pantalla. */
export function llegadosDelGuion(msDesdeQueAbrio: number, msPorProducto = 900): Map<number, string> {
  const cuantos = Math.min(TIENDA_DEL_GUION.length - 3, Math.floor(msDesdeQueAbrio / msPorProducto));
  return new Map(TIENDA_DEL_GUION.slice(0, Math.max(0, cuantos)).map((p) => [p.id, `guion-variante-${p.id}`]));
}
